<template>
    <div class="login-body">
        <div class="card login-panel p-fluid">
            <div class="login-panel-content">
                <div class="grid mt-4">
                    <Card>
                        <template v-slot:content>
                            <div class="col-12 text-center">
                                <img :src="`${baseUrl}/images/logo-academico.png`" class="img-responsive" alt="ACADEMICO" height="100"/>
                            </div>
                            <div class="col-12">
                                <h6 class="text-center">SISTEMA DE GESTION ACADEMICA</h6>
                            </div>

                            <div class="col-12" style="text-align:left">
                                <label class="login-label">Tipo de Usuario / Rol</label>
                                <div class="login-input">
                                    <Dropdown 
                                        v-model="selectedRole" 
                                        :options="roles" 
                                        placeholder="Seleccione su rol" 
                                        @change="resetFields"
                                    />
                                </div>
                            </div>

                            <div class="col-12" style="text-align:left">
                                <label class="login-label">{{ isCIDriven ? 'Número de C.I.' : 'Usuario ó Correo' }}</label>
                                <div class="login-input">
                                    <div class="p-inputgroup">
                                        <span class="p-inputgroup-addon"><i class="pi pi-user"></i></span>
                                        <InputText 
                                            v-model="correo" 
                                            :class="{'p-invalid': fieldErrors.correo}" 
                                            :placeholder="isCIDriven ? 'Ingrese su CI' : 'ejemplo@correo.com'" 
                                            @input="validateField('correo')"
                                        />
                                    </div>
                                    <small v-if="fieldErrors.correo" class="p-error">{{ fieldErrors.correo }}</small>
                                </div>
                            </div>

                            <div class="col-12" style="text-align:left">
                                <label class="login-label">Contraseña</label>
                                <div class="login-input">
                                    <div class="p-inputgroup">
                                        <span class="p-inputgroup-addon"><i class="pi pi-unlock"></i></span>
                                        <InputText 
                                            v-model="password" 
                                            :class="{'p-invalid': fieldErrors.password}"
                                            type="password" 
                                            :placeholder="isCIDriven ? 'Su contraseña es su CI' : 'Ingrese contraseña'" 
                                            @input="validateField('password')" 
                                            @keyup.enter="login"
                                        />
                                    </div>
                                    <small v-if="fieldErrors.password" class="p-error">{{ fieldErrors.password }}</small>
                                </div>
                            </div>

                            <div class="col-12 md:col-12 button-pane">
                                <Button label="Iniciar Sesión" @click="login" :loading="isLoading" :disabled="!selectedRole"/>
                            </div>
                        </template>
                    </Card>

                    <div class="col-12">
                        <small class="text-center">© 2026 ACADEMICO v1.0.0</small>
                    </div>
                </div>
            </div>
        </div>
        <Toast />
    </div>
</template>

<script>
import { useAuthStore } from '../../stores/authStore';
import { AuthService } from '../../service/AuthService';
import { loginSchema } from "./validations/loginValidate";

export default {
    name: 'Login',
    setup() {
        const authStore = useAuthStore();
        return { authStore };
    },
    data() {
        return {
            baseUrl: process.env.VUE_APP_BASE_URL || '',
            correo: '',
            password: '',
            isLoading: false,
            fieldErrors: {},
            // NUEVOS DATOS
            selectedRole: 'Docente', // Rol por defecto
            roles: [
                'Administrador',
                'Director',
                'Secretaria',
                'Docente',
                'Estudiante',
                'Tutor de estudiante',
            ]
        };
    },
    computed: {
        // Lógica para saber si el login es por CI
        isCIDriven() {
            const ciRoles = ['Docente', 'Estudiante', 'Tutor de estudiante'];
            return ciRoles.includes(this.selectedRole);
        }
    },
    methods: {
        resetFields() {
            this.correo = '';
            this.password = '';
            this.fieldErrors = {};
        },

        async login() {
            // 1. Validar campos básicos
            if (!(await this.validateLogin())) {
                this.$toast.add({ severity: 'info', summary: 'Atención', detail: 'Completa el formulario correctamente', life: 3000 });
                return;
            }

            this.isLoading = true;

            // 2. Lógica de transformación
            let finalUser = this.correo;
            let finalPass = this.password;

            if (this.isCIDriven) {
                // Si el usuario solo puso su CI, le concatenamos el dominio
                if (!this.correo.includes('@')) {
                    finalUser = `${this.correo}@insoftbo.com`;
                }
                // Si el campo contraseña está vacío pero es CI driven, 
                // asumimos que el password es el mismo CI (según tu requerimiento)
                if (!this.password) {
                    finalPass = this.correo;
                }
            }

            try {
                // Enviamos los datos procesados al servicio
                const response = await AuthService.login(finalUser, finalPass);
                const { access_token, user, expires_in } = response.data;
                
                
                // const token = response.data.access_token; 
                // const user = response.data.user; 

                if (access_token && user) {
                    this.authStore.login(access_token, user, expires_in);
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: '¡Éxito!', 
                        detail: `Bienvenido ${user.name}`, 
                        life: 2000 
                    });
                    setTimeout(() => this.$router.push({ name: 'home' }), 500);
                }
            } catch (error) {
                this.handleLoginError(error);
            } finally {
                this.isLoading = false;
            }
        },
        async validateField(field) {
            try {
                // AQUÍ se usa la variable. Si el nombre es distinto, fallará.
                await loginSchema.validateAt(field, { 
                    correo: this.correo, 
                    password: this.password 
                });
                this.fieldErrors[field] = '';
            } catch (error) {
                this.fieldErrors[field] = error.message;
            }
        },

        async validateLogin() {
            // Si el rol es Docente, Estudiante o Tutor, solo verificamos que no estén vacíos
            if (this.isCIDriven) {
                if (!this.correo) {
                    this.fieldErrors.correo = 'El número de CI es obligatorio';
                    return false;
                }
                this.fieldErrors = {};
                return true;
            }

            // Si es Admin, Director o Secretaria, usamos el esquema de Yup (loginSchema)
            try {
                await loginSchema.validate(
                    { correo: this.correo, password: this.password }, 
                    { abortEarly: false }
                );
                this.fieldErrors = {};
                return true;
            } catch (errors) {
                const formattedErrors = {};
                errors.inner.forEach((error) => { formattedErrors[error.path] = error.message; });
                this.fieldErrors = formattedErrors;
                return false;
            }
        },
        handleLoginError(error) {
            let mensaje = 'Error al conectar con el servidor';
            
            if (error.response) {
                // El servidor respondió con un código de error (401, 422, 500, etc.)
                mensaje = error.response.data.error || error.response.data.message || 'Credenciales inválidas';
            } else if (error.request) {
                // La petición se hizo pero no hubo respuesta
                mensaje = 'No se pudo establecer conexión con el servidor';
            }

            this.$toast.add({ 
                severity: 'error', 
                summary: 'Error de Acceso', 
                detail: mensaje, 
                life: 4000 
            });
        },

        // ... mantener validateField, validateLogin y handleLoginError igual ...
    }
};
</script>
<style scoped>
/* Forzar que el card no tenga espacios internos extraños si p-0 no es suficiente */


/* Asegurar que la imagen sea centrada y responsive */
.img-responsive {
    max-width: 50%;
    height: auto;
    margin: 0 auto;
}
</style>